import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Form,
    Input,
    Select,
    Radio,
    DatePicker,
    Upload,
    Button,
    message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { parseCSVForProfileIds } from "../../services/couponApi";
import { checkCouponCode } from "../../services/couponApi";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface CouponFormProps {
    form: any;
    onFinish: (values: any) => void;
    loading?: boolean;
}

const CouponForm: React.FC<CouponFormProps> = ({ form, onFinish, loading }) => {
    const [csvFile, setCsvFile] = useState<UploadFile[]>([]);
    const [parsedProfileIds, setParsedProfileIds] = useState<string[]>([]);
    const [checkingCode, setCheckingCode] = useState(false);
    const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Expose parsedProfileIds to parent via form instance
    useEffect(() => {
        (form as any).parsedProfileIds = parsedProfileIds;
    }, [parsedProfileIds, form]);

    const normalizeCouponCode = (value: string) => {
        if (!value) return value;
        return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    };

    const handleCSVUpload = async (file: File) => {
        try {
            const profileIds = await parseCSVForProfileIds(file);
            setParsedProfileIds(profileIds);
            message.success(
                `Successfully parsed ${profileIds.length} profile IDs from CSV`,
            );
            return false;
        } catch (error: any) {
            message.error(error.message || "Failed to parse CSV file");
            setCsvFile([]);
            setParsedProfileIds([]);
            return false;
        }
    };

    // Debounced coupon code check
    const checkCodeAvailability = useCallback(
        async (code: string) => {
            if (!code || code.length < 4) {
                setCodeAvailable(null);
                return;
            }

            setCheckingCode(true);
            try {
                const response = await checkCouponCode(code);
                const exists = response.exists || false;
                setCodeAvailable(!exists);

                if (exists) {
                    form.setFields([
                        {
                            name: "code",
                            errors: ["Coupon code already exists"],
                        },
                    ]);
                } else {
                    form.setFields([
                        {
                            name: "code",
                            errors: [],
                        },
                    ]);
                }
            } catch (error) {
                console.error("Error checking coupon code:", error);
                setCodeAvailable(null);
            } finally {
                setCheckingCode(false);
            }
        },
        [form],
    );

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        form.setFieldValue("code", value);

        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer for debounced check
        if (value.length >= 4) {
            debounceTimerRef.current = setTimeout(() => {
                checkCodeAvailability(value);
            }, 500); // 500ms debounce
        } else {
            setCodeAvailable(null);
        }
    };

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const validateCouponCode = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error("Please enter a coupon code"));
        }
        if (value.length < 4) {
            return Promise.reject(
                new Error("Coupon code must be at least 4 characters"),
            );
        }
        const isValid = /^[A-Z0-9]+$/.test(value);
        if (!isValid) {
            return Promise.reject(
                new Error("Only letters and numbers are allowed"),
            );
        }
        // If we've checked and it exists, reject
        if (codeAvailable === false) {
            return Promise.reject(new Error("Coupon code already exists"));
        }
        return Promise.resolve();
    };

    const validateDiscount = (_: any, value: number) => {
        const discountType = form.getFieldValue("type");
        if (discountType === "PERCENTAGE_OFF" && value > 100) {
            return Promise.reject(new Error("Percentage cannot exceed 100%"));
        }
        if (value <= 0) {
            return Promise.reject(
                new Error("Discount value must be greater than 0"),
            );
        }
        return Promise.resolve();
    };

    const validateDateRange = (_: any, value: any) => {
        if (!value || !value[0] || !value[1]) {
            return Promise.reject(new Error("Please select date range"));
        }
        if (value[1].isBefore(value[0])) {
            return Promise.reject(
                new Error("End date must be after start date"),
            );
        }
        return Promise.resolve();
    };

    const getCodeStatus = () => {
        const code = form.getFieldValue("code");
        if (!code || code.length < 4) return null;
        if (checkingCode) return "validating";
        if (codeAvailable === true) return "success";
        if (codeAvailable === false) return "error";
        return null;
    };

    const getCodeHelp = () => {
        const code = form.getFieldValue("code");
        if (!code || code.length < 4) {
            return "Minimum 4 characters. Only letters and numbers. Auto-converts to UPPERCASE.";
        }
        if (checkingCode) return "Checking availability...";
        if (codeAvailable === true) return "✓ Code available";
        if (codeAvailable === false) return "Code already exists";
        return "Only letters and numbers. Auto-converts to UPPERCASE.";
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
                userType: "ALL_USERS",
                regions: ["in", "us", "oc", "eu", "sa", "af"],
            }}
        >
            {/* Coupon Code */}
            <Form.Item
                label="Coupon Code"
                name="code"
                rules={[
                    { required: true, message: "Please enter a coupon code" },
                    { min: 4, message: "Minimum 4 characters required" },
                    { validator: validateCouponCode },
                ]}
                validateStatus={getCodeStatus() as any}
                hasFeedback
                help={getCodeHelp()}
            >
                <Input
                    placeholder="e.g., FLAT100"
                    onChange={handleCodeChange}
                    minLength={4}
                    maxLength={200}
                />
            </Form.Item>

            {/* Date Range */}
            <Form.Item
                label="Valid Period"
                name="dateRange"
                rules={[
                    { required: true, message: "Please select date range" },
                    { validator: validateDateRange },
                ]}
            >
                <RangePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    style={{ width: "100%" }}
                />
            </Form.Item>

            {/* Discount Type & Value */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                }}
            >
                <Form.Item
                    label="Discount Type"
                    name="type"
                    rules={[
                        {
                            required: true,
                            message: "Please select discount type",
                        },
                    ]}
                >
                    <Select placeholder="Select Type">
                        <Option value="FLAT_OFF">Flat Off</Option>
                        <Option value="PERCENTAGE_OFF">Percentage Off</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Discount Value"
                    name="value"
                    rules={[
                        {
                            required: true,
                            message: "Please enter discount value",
                        },
                        { validator: validateDiscount },
                    ]}
                >
                    <Input
                        type="number"
                        min={1}
                        placeholder="e.g., 100 or 50"
                    />
                </Form.Item>
            </div>

            {/* User Type */}
            <Form.Item
                label="Applicable Users"
                name="userType"
                rules={[{ required: true, message: "Please select user type" }]}
            >
                <Radio.Group>
                    <Radio value="ALL_USERS">All Users</Radio>
                    <Radio value="LIST_OF_USERS">Specific Users (CSV)</Radio>
                </Radio.Group>
            </Form.Item>

            {/* CSV Upload - Conditional */}
            <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) =>
                    getFieldValue("userType") === "LIST_OF_USERS" ? (
                        <Form.Item
                            label="Upload User List"
                            extra={
                                parsedProfileIds.length > 0
                                    ? `✓ ${parsedProfileIds.length} profile IDs loaded`
                                    : "CSV must have a column named 'profile_id'"
                            }
                            rules={[
                                {
                                    validator: async () => {
                                        if (parsedProfileIds.length === 0) {
                                            return Promise.reject(
                                                "Please upload a CSV file",
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Upload
                                fileList={csvFile}
                                onChange={({ fileList }) => {
                                    setCsvFile(fileList);
                                    if (fileList.length === 0) {
                                        setParsedProfileIds([]);
                                    }
                                }}
                                beforeUpload={handleCSVUpload}
                                accept=".csv"
                                maxCount={1}
                                onRemove={() => {
                                    setParsedProfileIds([]);
                                }}
                            >
                                <Button icon={<UploadOutlined />}>
                                    Upload CSV
                                </Button>
                            </Upload>
                        </Form.Item>
                    ) : null
                }
            </Form.Item>

            {/* Regions */}
            <Form.Item
                label="Applicable Regions"
                name="regions"
                rules={[
                    {
                        required: true,
                        message: "Please select at least one region",
                    },
                ]}
                extra="Select at least one region"
            >
                <Select
                    mode="multiple"
                    placeholder="Select regions"
                    style={{ width: "100%" }}
                >
                    <Option value="in">India (in)</Option>
                    <Option value="us">United States (us)</Option>
                    <Option value="oc">Oceania (oc)</Option>
                    <Option value="eu">Europe (eu)</Option>
                    <Option value="sa">South America (sa)</Option>
                    <Option value="af">Africa (af)</Option>
                </Select>
            </Form.Item>

            {/* Applicable Plans */}
            <Form.Item
                label="Applicable Plans"
                name="plans"
                rules={[
                    {
                        required: true,
                        message: "Please select at least one plan",
                    },
                ]}
                extra="Select at least one plan"
            >
                <Select
                    mode="multiple"
                    placeholder="Select plans"
                    style={{ width: "100%" }}
                >
                    <Option value="PAID_PLAN_1">PAID_PLAN_1</Option>
                    <Option value="IMC_QRP">IMC_QRP</Option>
                    <Option value="BASIC_PLAN">BASIC_PLAN</Option>
                    <Option value="PRO_PLAN">PRO_PLAN</Option>
                    <Option value="ENTERPRISE_PLAN">ENTERPRISE_PLAN</Option>
                </Select>
            </Form.Item>
        </Form>
    );
};

export default CouponForm;
export { type CouponFormProps };
